import { t } from "@/lib/i18n/interpolate";
import { authorLabel, type NameStyle } from "@/lib/tool-kit/publishing/authors";
import type { RichParagraph } from "@/lib/tool-kit/publishing/rich-text";

/**
 * CRediT — the Contributor Roles Taxonomy, ANSI/NISO Z39.104-2022.
 *
 * Fourteen roles, in the order the standard lists them (alphabetical). The ids are
 * machine keys; the printed names come from the widget's strings, whose English values
 * are the standard's names exactly ("Writing – original draft", with an en dash).
 *
 * The degree of contribution — lead, equal or supporting — is an optional qualifier
 * NISO describes for a role several people share. It is not part of the standard
 * itself, and publishers decide whether to ask for it, so it is off unless the writer
 * turns it on.
 */
export const CREDIT_ROLE_IDS = [
  "conceptualization",
  "dataCuration",
  "formalAnalysis",
  "fundingAcquisition",
  "investigation",
  "methodology",
  "projectAdministration",
  "resources",
  "software",
  "supervision",
  "validation",
  "visualization",
  "writingOriginalDraft",
  "writingReviewEditing",
] as const;

export type CreditRoleId = (typeof CREDIT_ROLE_IDS)[number];

/** "yes" is a contribution with no degree stated. */
export type CreditDegree = "yes" | "lead" | "equal" | "supporting";

export interface CreditContributor {
  name: string;
  roles: Partial<Record<CreditRoleId, CreditDegree>>;
}

export type CreditLayout = "author" | "role";

export interface CreditTemplates {
  roles: Readonly<Record<CreditRoleId, string>>;
  degrees: Readonly<Record<Exclude<CreditDegree, "yes">, string>>;
  /** "{item} ({degree})" */
  withDegree: string;
  /** Between the names or roles inside one entry: ", " */
  itemSeparator: string;
  /** Closes the bold label of an entry: ":" */
  labelEnd: string;
  /** Closes an entry: "." */
  entryEnd: string;
}

export interface CreditOptions {
  layout: CreditLayout;
  nameStyle: NameStyle;
  showDegrees: boolean;
}

/** One entry of the statement: "Jane Smith: Conceptualization, Methodology." */
export interface CreditEntry {
  label: string;
  items: string[];
}

function qualified(
  item: string,
  degree: CreditDegree,
  showDegrees: boolean,
  templates: CreditTemplates
): string {
  if (!showDegrees || degree === "yes") {
    return item;
  }
  return t(templates.withDegree, { item, degree: templates.degrees[degree] });
}

/** Contributors with a name, in the order they were entered. */
function named(contributors: readonly CreditContributor[]) {
  return contributors.filter((contributor) => contributor.name.trim() !== "");
}

function byAuthor(
  contributors: readonly CreditContributor[],
  options: CreditOptions,
  templates: CreditTemplates
): CreditEntry[] {
  const entries: CreditEntry[] = [];
  for (const contributor of named(contributors)) {
    const items: string[] = [];
    for (const role of CREDIT_ROLE_IDS) {
      const degree = contributor.roles[role];
      if (degree) {
        items.push(
          qualified(
            templates.roles[role],
            degree,
            options.showDegrees,
            templates
          )
        );
      }
    }
    if (items.length > 0) {
      entries.push({
        label: authorLabel(contributor.name, options.nameStyle),
        items,
      });
    }
  }
  return entries;
}

function byRole(
  contributors: readonly CreditContributor[],
  options: CreditOptions,
  templates: CreditTemplates
): CreditEntry[] {
  const entries: CreditEntry[] = [];
  for (const role of CREDIT_ROLE_IDS) {
    const items: string[] = [];
    for (const contributor of named(contributors)) {
      const degree = contributor.roles[role];
      if (degree) {
        items.push(
          qualified(
            authorLabel(contributor.name, options.nameStyle),
            degree,
            options.showDegrees,
            templates
          )
        );
      }
    }
    if (items.length > 0) {
      entries.push({ label: templates.roles[role], items });
    }
  }
  return entries;
}

/** The statement's entries — by author (Elsevier's layout) or by role (PLOS's). */
export function creditEntries(
  contributors: readonly CreditContributor[],
  options: CreditOptions,
  templates: CreditTemplates
): CreditEntry[] {
  return options.layout === "author"
    ? byAuthor(contributors, options, templates)
    : byRole(contributors, options, templates);
}

/**
 * Entries as rich paragraphs, the label of each in bold. One paragraph holding every
 * entry, or one paragraph per entry when `perLine` is set.
 */
export function creditParagraphs(
  entries: readonly CreditEntry[],
  templates: CreditTemplates,
  perLine: boolean
): RichParagraph[] {
  const paragraphs = entries.map((entry, index): RichParagraph => {
    const items = entry.items.join(templates.itemSeparator);
    // Initials already end in a full stop: "P.R." closes the entry by itself.
    const end = items.endsWith(templates.entryEnd) ? "" : templates.entryEnd;
    return [
      {
        text: `${index > 0 && !perLine ? " " : ""}${entry.label}${templates.labelEnd}`,
        bold: true,
      },
      { text: ` ${items}${end}` },
    ];
  });
  return perLine ? paragraphs : [paragraphs.flat()];
}

/** Named contributors who have not been given a role yet. */
export function contributorsWithoutRoles(
  contributors: readonly CreditContributor[]
): string[] {
  return named(contributors)
    .filter((contributor) => Object.keys(contributor.roles).length === 0)
    .map((contributor) => contributor.name.trim());
}

/** Roles nobody holds — often fine (not every study has software), worth a glance. */
export function unassignedRoles(
  contributors: readonly CreditContributor[]
): CreditRoleId[] {
  const people = named(contributors);
  return CREDIT_ROLE_IDS.filter(
    (role) => !people.some((contributor) => contributor.roles[role])
  );
}
